<template>
<div class="pf-root" :style="themePageStyle">

  <nav class="pf-topbar">
    <div class="pf-tb-inner">
      <button class="pf-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" class="pf-logo" /></a>
      <span class="pf-tb-title">Profile</span>
      <button class="pf-tb-feed" @click="$router.push('/forum')"><i class="mdi mdi-home"></i><span class="pf-tb-feed-lbl"> Feed</span></button>
    </div>
  </nav>

  <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

  <div v-else-if="!profile" class="pf-empty">
    <i class="mdi mdi-account-off-outline"></i>
    <h5>User not found</h5>
    <button class="btn btn-warning" @click="$router.push('/forum')">Back to feed</button>
  </div>

  <div v-else class="pf-wrap">
    <!-- header -->
    <div class="pf-header">
      <div class="pf-cover"></div>
      <div class="pf-id">
        <div class="pf-avatar"><user-avatar :src="profile.avatar_url" :name="profile.name" size="xl" /></div>
        <div class="pf-id-body">
          <h2 class="pf-name">
            {{ profile.name && profile.name.trim() ? profile.name : 'A farmer' }}
            <span v-if="Number(profile.is_vet)>0" class="pf-vet"><i class="mdi mdi-stethoscope"></i> Vet</span>
          </h2>
          <div class="pf-joined"><i class="mdi mdi-calendar"></i> Joined {{ joined }}</div>
          <div class="pf-stats">
            <span><strong>{{ profile.posts }}</strong> posts</span>
            <span><strong>{{ profile.followers }}</strong> followers</span>
            <span><strong>{{ profile.following }}</strong> following</span>
          </div>
        </div>
        <div class="pf-actions" v-if="!Number(profile.is_me)">
          <button class="pf-follow" :class="{following:profile.i_follow}" @click="toggleFollow">
            <i class="mdi" :class="profile.i_follow?'mdi-account-check':'mdi-account-plus'"></i>
            {{ profile.i_follow ? 'Following' : 'Follow' }}
          </button>
          <button class="pf-msg" @click="messageUser" :disabled="msgBusy"><span v-if="msgBusy" class="spinner-border spinner-border-sm"></span><i v-else class="mdi mdi-message-text"></i> Message</button>
        </div>
        <div class="pf-actions" v-else>
          <button class="pf-msg" @click="$router.push('/me')"><i class="mdi mdi-pencil"></i> Edit profile</button>
        </div>
      </div>
    </div>

    <!-- posts -->
    <div class="pf-section-head">{{ Number(profile.is_me) ? 'Your posts' : 'Posts' }}</div>
    <div v-if="!posts.length" class="pf-noposts">
      <i class="mdi mdi-postage-stamp"></i>
      <p>{{ Number(profile.is_me) ? "You haven't posted yet." : "No posts yet." }}</p>
    </div>
    <div v-else>
      <div v-for="p in posts" :key="p.id" class="pf-post" @click="$router.push('/forum/'+p.id)">
        <div class="pf-post-head">
          <user-avatar :src="p.author_avatar" :name="p.author_name" size="md" />
          <div class="pf-post-head-body">
            <div class="pf-post-author">{{ p.author_name && p.author_name.trim() ? p.author_name : 'A farmer' }}<span v-if="p.shared_topic_id" class="pf-shared-lbl"> shared a post</span></div>
            <div class="pf-post-meta">{{ relTime(p.created_at) }}<i class="mdi mdi-earth"></i><span v-if="p.location_label" class="pf-loc"><i class="mdi mdi-map-marker"></i>{{ p.location_label }}</span></div>
          </div>
        </div>
        <div v-if="p.content" class="pf-post-body">{{ p.content }}</div>
        <div v-if="parseMedia(p.photos).length" class="pf-post-media" :class="'n'+Math.min(parseMedia(p.photos).length,4)">
          <template v-for="(m,mi) in parseMedia(p.photos)" :key="mi">
            <img v-if="m.type==='image'||!m.type" :src="mediaUrl(m)" class="pf-media-cell" @click.stop="lightbox=mediaUrl(m)" />
            <video v-else-if="m.type==='video'" :src="mediaUrl(m)" class="pf-media-cell" controls preload="metadata" @click.stop></video>
            <a v-else :href="mediaUrl(m)" target="_blank" class="pf-doc" @click.stop><i class="mdi" :class="docIcon(m.name)"></i> {{ m.name||'Document' }}</a>
          </template>
        </div>
        <div v-if="p.shared_topic_id && p.shared_content!=null" class="pf-shared">
          <div class="pf-shared-author"><i class="mdi mdi-share-variant me-1"></i>{{ p.shared_author_name||'A farmer' }}</div>
          <div v-if="p.shared_content" class="pf-shared-body">{{ p.shared_content }}</div>
        </div>
        <div class="pf-post-counts">
          <span v-if="Number(p.reaction_count)>0"><i class="mdi mdi-thumb-up"></i> {{ p.reaction_count }}</span>
          <span v-if="Number(p.comment_count)>0">{{ p.comment_count }} comment{{ Number(p.comment_count)>1?'s':'' }}</span>
          <span v-if="Number(p.share_count)>0">{{ p.share_count }} share{{ Number(p.share_count)>1?'s':'' }}</span>
        </div>
      </div>
    </div>
  </div>

  <div v-if="lightbox" class="pf-lightbox" @click="lightbox=null"><img :src="lightbox" /></div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F0EBE4;--fm-bg3:#E9E4DC;--fm-br:#E2DCD3}
html[data-theme="light"] body{background:#EBE7E1!important}
html[data-theme="light"] .pf-root{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .pf-topbar{background:#FFFFFF!important;border-bottom-color:#E2DCD3!important}
html[data-theme="light"] .pf-header,
html[data-theme="light"] .pf-post{background:#FFFFFF!important;border-color:#E2DCD3!important}
html[data-theme="light"] .pf-shared{background:#EBE7E1!important}
</style>

<style scoped>
.pf-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.pf-topbar{position:sticky;top:0;z-index:100;background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.pf-tb-inner{display:flex;align-items:center;gap:12px;max-width:680px;margin:0 auto;padding:10px 14px}
.pf-back{width:38px;height:38px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:20px}
.pf-logo{height:30px}
.pf-tb-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;flex:1}
.pf-tb-feed{background:rgba(191,161,100,.12);border:none;color:#BFA164;font-weight:700;font-size:13px;padding:8px 12px;border-radius:8px}
.pf-wrap{max-width:680px;margin:0 auto;padding:16px 14px 60px}
.pf-empty{text-align:center;padding:60px 20px}
.pf-empty i{font-size:64px;color:var(--fm-s,#3A3530)}
.pf-empty h5{color:var(--fm-h,#F5F0E8);margin:12px 0}

.pf-header{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:16px;overflow:hidden;margin-bottom:16px}
.pf-cover{height:120px;background:linear-gradient(120deg,#2a2418,#3a2f1a,#14110a)}
.pf-id{padding:0 20px 18px;display:flex;flex-wrap:wrap;align-items:flex-end;gap:14px}
.pf-avatar{margin-top:-44px;border:4px solid var(--fm-bg1,#141414);border-radius:50%;background:var(--fm-bg1,#141414)}
.pf-id-body{flex:1;min-width:0;padding-top:8px}
.pf-name{color:var(--fm-h,#F5F0E8);font-weight:800;font-size:22px;margin:0;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.pf-vet{display:inline-flex;align-items:center;gap:3px;background:rgba(79,195,247,.14);color:#4FC3F7;font-size:11px;font-weight:700;padding:2px 8px;border-radius:6px}
.pf-joined{color:var(--fm-s,#6B6356);font-size:12px;margin-top:3px}
.pf-joined i{font-size:13px}
.pf-stats{display:flex;gap:16px;margin-top:8px;color:var(--fm-m,#A09888);font-size:13px}
.pf-stats strong{color:var(--fm-h,#F5F0E8)}
.pf-actions{display:flex;gap:8px;padding-top:8px}
.pf-follow{background:#BFA164;border:none;color:#0A0A0A;font-weight:700;font-size:13px;padding:8px 16px;border-radius:8px;display:flex;align-items:center;gap:5px}
.pf-follow.following{background:var(--fm-bg3,#1E1E1E);color:var(--fm-m,#A09888)}
.pf-msg{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;padding:8px 16px;border-radius:8px;display:flex;align-items:center;gap:5px}

.pf-section-head{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:16px;margin:6px 4px 12px}
.pf-noposts{text-align:center;padding:40px 20px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;color:var(--fm-s,#6B6356)}
.pf-noposts i{font-size:48px;color:var(--fm-s,#3A3530)}

.pf-post{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:14px;margin-bottom:14px;cursor:pointer}
.pf-post:hover{border-color:#BFA164}
.pf-post-head{display:flex;align-items:center;gap:10px}
.pf-post-head-body{flex:1;min-width:0}
.pf-post-author{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.pf-shared-lbl{color:var(--fm-m,#A09888);font-weight:400}
.pf-post-meta{display:flex;align-items:center;gap:5px;color:var(--fm-s,#6B6356);font-size:12px;margin-top:1px}
.pf-post-meta i{font-size:13px}
.pf-loc{display:inline-flex;align-items:center;gap:2px;color:#66BB6A}
.pf-post-body{color:var(--fm-h,#F5F0E8);font-size:14px;line-height:1.5;white-space:pre-wrap;margin:10px 0}
.pf-post-media{display:grid;gap:3px;margin:10px -14px 0}
.pf-post-media.n1{grid-template-columns:1fr}
.pf-post-media.n2,.pf-post-media.n3,.pf-post-media.n4{grid-template-columns:1fr 1fr}
.pf-post-media.n3>*:first-child{grid-column:1/-1}
.pf-media-cell{width:100%;height:100%;max-height:420px;object-fit:cover;background:var(--fm-bg2,#0F0F0F);display:block}
.pf-post-media.n1 .pf-media-cell{max-height:520px;object-fit:contain}
.pf-doc{display:flex;align-items:center;gap:8px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px;color:#BFA164;text-decoration:none;font-size:13px;margin-top:8px}
.pf-shared{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:10px 12px;margin-top:10px}
.pf-shared-author{color:#BFA164;font-size:12px;font-weight:700;margin-bottom:3px}
.pf-shared-body{color:var(--fm-m,#A09888);font-size:13px}
.pf-post-counts{display:flex;gap:12px;font-size:12px;color:var(--fm-s,#6B6356);padding-top:10px;margin-top:10px;border-top:1px solid var(--fm-br,#1E1E1E)}
.pf-post-counts i{color:#4FC3F7}
.pf-lightbox{position:fixed;inset:0;background:rgba(0,0,0,.92);display:flex;align-items:center;justify-content:center;z-index:2500;padding:20px}
.pf-lightbox img{max-width:100%;max-height:100%;border-radius:8px}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  /* Topbar: tighter spacing, hide "Feed" label */
  .pf-tb-inner{gap:8px;padding:8px 10px}
  .pf-back{width:34px;height:34px;font-size:18px}
  .pf-logo{height:26px}
  .pf-tb-title{font-size:14px}
  .pf-tb-feed{padding:6px 9px;font-size:12px}
  .pf-tb-feed-lbl{display:none}

  /* Page padding + safe-area for bottom nav */
  .pf-wrap{padding:12px 10px calc(60px + env(safe-area-inset-bottom, 0px))}

  /* Header card: smaller cover, less padding, tighter avatar */
  .pf-header{border-radius:12px;margin-bottom:12px}
  .pf-cover{height:80px}
  .pf-id{padding:0 14px 14px;gap:10px}
  .pf-avatar{margin-top:-34px;border-width:3px}
  .pf-id-body{padding-top:6px;flex-basis:100%}
  .pf-name{font-size:18px;gap:6px}
  .pf-vet{font-size:10px;padding:2px 6px}
  .pf-joined{font-size:11px}
  .pf-stats{gap:12px;margin-top:6px;font-size:12px;flex-wrap:wrap}
  .pf-actions{padding-top:6px;width:100%}
  .pf-actions .pf-follow,
  .pf-actions .pf-msg{flex:1;justify-content:center;padding:9px 12px;font-size:12px}

  /* Posts list */
  .pf-section-head{font-size:14px;margin:4px 2px 10px}
  .pf-post{padding:12px;border-radius:10px;margin-bottom:10px}
  .pf-post-author{font-size:13px}
  .pf-post-body{font-size:13px;line-height:1.45;margin:8px 0}
  .pf-post-media{margin:8px -12px 0}
  .pf-media-cell{max-height:280px}
  .pf-post-media.n1 .pf-media-cell{max-height:380px}
  .pf-shared{padding:8px 10px}
  .pf-shared-body{font-size:12px}
  .pf-post-counts{font-size:11px;gap:10px;padding-top:8px;margin-top:8px}
  .pf-lightbox{padding:10px}
}

/* Extra-small phones */
@media (max-width: 380px) {
  .pf-tb-title{display:none}
  .pf-stats{gap:8px;font-size:11px}
  .pf-name{font-size:16px}
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

export default {
  name: "ProfilePage",
  components: { UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null, loading:true, profile:null, posts:[], lightbox:null, msgBusy:false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#EBE7E1;min-height:100vh;color:#1A1A1A;'},
    myUserId(){return sessionStorage.getItem('userId')||''},
    joined(){ if(!this.profile||!this.profile.created_at) return ''; var d=new Date(String(this.profile.created_at).replace(' ','T')); return d.toLocaleDateString('en',{month:'long',year:'numeric'}); }
  },
  watch:{ '$route.params.id'(){ this.load(); } },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.load();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/forum'); },
    parseMedia(p){ if(!p)return[]; if(Array.isArray(p))return p; try{var a=JSON.parse(p);return Array.isArray(a)?a:[]}catch(e){return[]} },
    mediaUrl(m){ var u = typeof m==='string'?m:(m&&(m.url||m.path)||''); if(!u)return''; if(u.indexOf('http')===0)return u; return uploads+String(u).replace(/^uploads\//,''); },
    docIcon(name){ var n=(name||'').toLowerCase(); if(n.indexOf('.pdf')>-1)return'mdi-file-pdf-box'; if(/\.docx?$/.test(n))return'mdi-file-word-box'; if(/\.xlsx?$/.test(n)||n.indexOf('.csv')>-1)return'mdi-file-excel-box'; return'mdi-file-outline'; },
    relTime(ts){
      if(!ts) return '';
      var diff = Math.floor((Date.now() - new Date(ts.replace(' ','T')).getTime())/1000);
      if(diff<60) return 'just now';
      if(diff<3600) return Math.floor(diff/60)+'m';
      if(diff<86400) return Math.floor(diff/3600)+'h';
      if(diff<2592000) return Math.floor(diff/86400)+'d';
      return ts.split(' ')[0];
    },
    async load(){
      this.loading=true; this.profile=null; this.posts=[];
      try{
        var r=await executeJson({function:'getPublicProfile',userId:this.$route.params.id,viewerId:this.myUserId},flocker);
        if(r.data&&r.data.success){ this.profile=r.data.data.profile; this.posts=r.data.data.posts||[]; }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    messageUser(){
      if(!this.myUserId){this.$router.push('/login?redirect=/u/'+this.$route.params.id);return}
      if(!this.profile || this.profile.id===this.myUserId) return;
      // Pop open the Messenger-style chat dock without leaving this page.
      window.dispatchEvent(new CustomEvent('flockr-open-chat', { detail: {
        userId: this.profile.id,
        name: this.profile.name || this.profile.display_name || 'Farmer',
        avatar: this.profile.avatar_url || ''
      }}));
    },
    async toggleFollow(){
      if(!this.myUserId){this.$router.push('/login?redirect=/u/'+this.$route.params.id);return}
      var fn=this.profile.i_follow?'unfollowUser':'followUser';
      try{var r=await executeJson({function:fn,followerId:this.myUserId,followingId:this.profile.id},flocker);
        if(r.data&&r.data.success){
          var nowF=!this.profile.i_follow;
          this.profile.i_follow=nowF?1:0;
          this.profile.followers=Math.max(0,(Number(this.profile.followers)||0)+(nowF?1:-1));
        }}catch(e){}
    }
  }
};
</script>
